// Localização: frontend/src/pages/Financeiro/ConfigFinanceiroPage.jsx
// Página de Configurações do Módulo Financeiro

import React, { useState, useEffect } from 'react';
import PageTitle from '../../components/Layout/PageTitle';
import Modal from '../../components/UI/Modal';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionContext';
import { useSearch } from '../../context/SearchContext';

// Hooks
import { useContasBancarias, useCategorias, useSubcategorias } from '../../hooks/useFinanceiro';

// Componentes
import AcessoRapidoFinanceiro from '../../components/Financeiro/AcessoRapidoFinanceiro';
import FormContaBancaria from '../../components/Financeiro/FormContaBancaria';
import FormCategoria from '../../components/Financeiro/FormCategoria';
import FormSubcategoria from '../../components/Financeiro/FormSubcategoria';
import ListaContasBancarias from '../../components/Financeiro/ListaContasBancarias';
import ListaCategorias from '../../components/Financeiro/ListaCategorias';
import ListaSubcategorias from '../../components/Financeiro/ListaSubcategorias';
import LoadingIndicator from '../../components/Common/LoadingIndicator';

const ConfigFinanceiroPage = () => {
    const { isAuthenticated } = useAuth();
    const { checkPermission } = usePermissions();
    const { setSearchPlaceholder } = useSearch();

    // Estado da aba ativa
    const [activeTab, setActiveTab] = useState('contas-bancarias');

    // Estados de Modal
    const [isContaModalOpen, setIsContaModalOpen] = useState(false);
    const [isCategoriaModalOpen, setIsCategoriaModalOpen] = useState(false);
    const [isSubcategoriaModalOpen, setIsSubcategoriaModalOpen] = useState(false);

    // Estados de edição
    const [editingConta, setEditingConta] = useState(null);
    const [editingCategoria, setEditingCategoria] = useState(null);
    const [editingSubcategoria, setEditingSubcategoria] = useState(null);

    // Trigger de refresh
    const [pageRefreshTrigger, setPageRefreshTrigger] = useState(0);
    const triggerRefresh = () => setPageRefreshTrigger(prev => prev + 1);

    // Hooks de dados
    const {
        contas,
        loading: loadingContas,
        fetchContas,
        createConta,
        updateConta,
        deleteConta
    } = useContasBancarias();

    const {
        categorias,
        loading: loadingCategorias,
        fetchCategorias,
        createCategoria,
        updateCategoria,
        deleteCategoria
    } = useCategorias();

    const {
        subcategorias,
        loading: loadingSubcategorias,
        fetchSubcategorias,
        createSubcategoria,
        updateSubcategoria,
        deleteSubcategoria
    } = useSubcategorias();

    // Permissões
    const canAccess = checkPermission('financeiro', 'can_access');
    const canCreate = checkPermission('financeiro', 'can_create');
    const canEdit = checkPermission('financeiro', 'can_edit');
    const canDelete = checkPermission('financeiro', 'can_delete');

    // Buscar dados quando a aba muda
    useEffect(() => {
        if (!isAuthenticated || !canAccess) return;

        if (activeTab === 'contas-bancarias') {
            fetchContas();
        } else if (activeTab === 'categorias') {
            fetchCategorias();
        } else if (activeTab === 'subcategorias') {
            // Sempre buscar categorias também para subcategorias
            fetchCategorias();
            fetchSubcategorias();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isAuthenticated,
        canAccess,
        activeTab,
        pageRefreshTrigger
        // fetchContas, fetchCategorias, fetchSubcategorias removidos - são estáveis com useCallback
    ]);

    // Configurar placeholder de busca
    useEffect(() => {
        if (activeTab === 'contas-bancarias') {
            setSearchPlaceholder('Buscar por nome, banco ou conta...');
        } else if (activeTab === 'categorias') {
            setSearchPlaceholder('Buscar por nome ou tipo...');
        } else if (activeTab === 'subcategorias') {
            setSearchPlaceholder('Buscar por nome ou categoria...');
        }
    }, [activeTab, setSearchPlaceholder]);

    // Handlers - Contas Bancárias
    const handleSaveConta = async (formData) => {
        try {
            if (editingConta) {
                await updateConta(editingConta.id, formData);
            } else {
                await createConta(formData);
            }
            setIsContaModalOpen(false);
            setEditingConta(null);
            triggerRefresh();
        } catch (error) {
            console.error('Erro ao salvar conta bancária:', error);
        }
    };

    const handleDeleteConta = async (id) => {
        if (!window.confirm('Tem certeza que deseja desativar esta conta bancária?')) return;
        try {
            await deleteConta(id);
            triggerRefresh();
        } catch (error) {
            console.error('Erro ao deletar conta bancária:', error);
        }
    };

    const handleOpenCreateConta = () => {
        setEditingConta(null);
        setIsContaModalOpen(true);
    };

    const handleOpenEditConta = (conta) => {
        setEditingConta(conta);
        setIsContaModalOpen(true);
    };

    // Handlers - Categorias
    const handleSaveCategoria = async (formData) => {
        try {
            if (editingCategoria) {
                await updateCategoria(editingCategoria.id, formData);
            } else {
                await createCategoria(formData);
            }
            setIsCategoriaModalOpen(false);
            setEditingCategoria(null);
            triggerRefresh();
        } catch (error) {
            console.error('Erro ao salvar categoria:', error);
        }
    };

    const handleDeleteCategoria = async (id) => {
        if (!window.confirm('Tem certeza que deseja desativar esta categoria?')) return;
        try {
            await deleteCategoria(id);
            triggerRefresh();
        } catch (error) {
            console.error('Erro ao deletar categoria:', error);
        }
    };

    const handleOpenCreateCategoria = () => {
        setEditingCategoria(null);
        setIsCategoriaModalOpen(true);
    };

    const handleOpenEditCategoria = (categoria) => {
        setEditingCategoria(categoria);
        setIsCategoriaModalOpen(true);
    };

    // Handlers - Subcategorias
    const handleSaveSubcategoria = async (formData) => {
        try {
            if (editingSubcategoria) {
                await updateSubcategoria(editingSubcategoria.id, formData);
            } else {
                await createSubcategoria(formData);
            }
            setIsSubcategoriaModalOpen(false);
            setEditingSubcategoria(null);
            triggerRefresh();
        } catch (error) {
            console.error('Erro ao salvar subcategoria:', error);
        }
    };

    const handleDeleteSubcategoria = async (id) => {
        if (!window.confirm('Tem certeza que deseja desativar esta subcategoria?')) return;
        try {
            await deleteSubcategoria(id);
            triggerRefresh();
        } catch (error) {
            console.error('Erro ao deletar subcategoria:', error);
        }
    };

    const handleOpenCreateSubcategoria = () => {
        setEditingSubcategoria(null);
        setIsSubcategoriaModalOpen(true);
    };

    const handleOpenEditSubcategoria = (subcategoria) => {
        setEditingSubcategoria(subcategoria);
        setIsSubcategoriaModalOpen(true);
    };

    if (!canAccess) {
        return (
            <>
                <PageTitle
                    title="Configurações Financeiras"
                    subtitle="Você não tem permissão para acessar as configurações financeiras"
                />
                <p className="text-red-500">Você não tem permissão para acessar este módulo.</p>
            </>
        );
    }

    return (
        <div className="min-h-screen w-full">
            {/* Acesso Rápido às Funções - Posição Fixa - Centralizado - Abaixo do Header */}
            <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-20 lg:top-20">
                <AcessoRapidoFinanceiro />
            </div>

            {/* Conteúdo com espaçamento para o AcessoRapido fixo */}
            <div className="pt-32 pb-8 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <PageTitle
                    title="Configurações Financeiras"
                    subtitle="Gerencie contas bancárias, categorias e subcategorias"
                />

                {/* Navegação por Abas */}
                <nav className="-mb-px flex flex-wrap gap-2 sm:gap-4 sm:space-x-4 sm:space-x-8 border-b border-gray-200" aria-label="Tabs">
                    <button
                        onClick={() => setActiveTab('contas-bancarias')}
                        className={`py-2 sm:py-4 px-2 sm:px-1 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${
                            activeTab === 'contas-bancarias'
                                ? 'border-emerald-500 text-emerald-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        <span className="hidden sm:inline">Contas Bancárias</span>
                        <span className="sm:hidden">Contas</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('categorias')}
                        className={`py-2 sm:py-4 px-2 sm:px-1 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${
                            activeTab === 'categorias'
                                ? 'border-emerald-500 text-emerald-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Categorias
                    </button>
                    <button
                        onClick={() => setActiveTab('subcategorias')}
                        className={`py-2 sm:py-4 px-2 sm:px-1 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${
                            activeTab === 'subcategorias'
                                ? 'border-emerald-500 text-emerald-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Subcategorias
                    </button>
                </nav>

                {/* Conteúdo das Abas */}
                <div className="pt-6">
                    {activeTab === 'contas-bancarias' && (
                        loadingContas ? (
                            <LoadingIndicator />
                        ) : (
                            <ListaContasBancarias
                                data={contas}
                                canCreate={canCreate}
                                canEdit={canEdit}
                                canDelete={canDelete}
                                handleOpenCreateModal={handleOpenCreateConta}
                                handleOpenEditModal={handleOpenEditConta}
                                handleDelete={handleDeleteConta}
                            />
                        )
                    )}

                    {activeTab === 'categorias' && (
                        loadingCategorias ? (
                            <LoadingIndicator />
                        ) : (
                            <ListaCategorias
                                data={categorias}
                                canCreate={canCreate}
                                canEdit={canEdit}
                                canDelete={canDelete}
                                handleOpenCreateModal={handleOpenCreateCategoria}
                                handleOpenEditModal={handleOpenEditCategoria}
                                handleDelete={handleDeleteCategoria}
                            />
                        )
                    )}

                    {activeTab === 'subcategorias' && (
                        loadingSubcategorias ? (
                            <LoadingIndicator />
                        ) : (
                            <ListaSubcategorias
                                data={subcategorias}
                                categorias={categorias}
                                canCreate={canCreate}
                                canEdit={canEdit}
                                canDelete={canDelete}
                                handleOpenCreateModal={handleOpenCreateSubcategoria}
                                handleOpenEditModal={handleOpenEditSubcategoria}
                                handleDelete={handleDeleteSubcategoria}
                                onCategoriasChange={fetchCategorias}
                            />
                        )
                    )}
                </div>
            </div>

            {/* Modais */}
            <Modal
                isOpen={isContaModalOpen}
                onClose={() => {
                    setIsContaModalOpen(false);
                    setEditingConta(null);
                }}
                title={editingConta ? 'Editar Conta Bancária' : 'Nova Conta Bancária'}
            >
                <FormContaBancaria
                    initialData={editingConta}
                    onSubmit={handleSaveConta}
                    onClose={() => {
                        setIsContaModalOpen(false);
                        setEditingConta(null);
                    }}
                    isEditing={!!editingConta}
                />
            </Modal>

            <Modal
                isOpen={isCategoriaModalOpen}
                onClose={() => {
                    setIsCategoriaModalOpen(false);
                    setEditingCategoria(null);
                }}
                title={editingCategoria ? 'Editar Categoria' : 'Nova Categoria'}
            >
                <FormCategoria
                    initialData={editingCategoria}
                    onSubmit={handleSaveCategoria}
                    onClose={() => {
                        setIsCategoriaModalOpen(false);
                        setEditingCategoria(null);
                    }}
                    isEditing={!!editingCategoria}
                />
            </Modal>

            <Modal
                isOpen={isSubcategoriaModalOpen}
                onClose={() => {
                    setIsSubcategoriaModalOpen(false);
                    setEditingSubcategoria(null);
                }}
                title={editingSubcategoria ? 'Editar Subcategoria' : 'Nova Subcategoria'}
            >
                <FormSubcategoria
                    initialData={editingSubcategoria}
                    categorias={categorias}
                    onSubmit={handleSaveSubcategoria}
                    onClose={() => {
                        setIsSubcategoriaModalOpen(false);
                        setEditingSubcategoria(null);
                    }}
                    isEditing={!!editingSubcategoria}
                />
            </Modal>
            </div>
        </div>
    );
};

export default ConfigFinanceiroPage;

